import {
    MutationCache,
    QueryCache,
    QueryClient,
    QueryClientProvider
} from "@tanstack/react-query";
import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import { AuthProvider } from "react-oidc-context";
import { PropagateProvider } from "./@api/PropagateProvider";
import { addExperimentalCallback } from "./@api/usePrefetch";
import { ErrorBoundary } from "./@error/ErrorBoundary";
import { isInvalidOrExpiredToken } from "./@error/errorCheck";
import { showError } from "./@error/ErrorManager";
import { GlobalErrorDisplay } from "./@error/GlobalErrorDisplay";
import { brandName } from "./@navigation/branding/brandName";
import { CustomHashRouter } from "./@navigation/CustomHashRouter";
import { PreloadProvider } from "./@navigation/PreloadProvider";
import { Router } from "./@navigation/Router";
import { ScrollRestorationProvider } from "./@navigation/ScrollRestoration";
import { LoadingBoundary } from "./@ui/LoadingBoundary";
import { SuspenseImageProvider } from "./@ui/SuspenseImage";
import { FirmProvider } from "./firm/FirmProvider";
import { LoggedInUserProvider } from "./user/LoggedInUserProvider";
import { ThemeProvider } from "./user/ThemeProvider";
import { onSigninCallback, userManager } from "./user/userManager";
import { Toaster } from "@/components/ui/toaster";
import "./index.css";

document.title = brandName;

const queryClient = new QueryClient(
    addExperimentalCallback({
        defaultOptions: {
            queries: {
                retry: 1,
                staleTime: 2 * 60 * 1000, // at least a small amount of staleTime is helpful for optimised prefetching, otherwise there will be unnecessary re-fetches.
                refetchInterval: 5 * 60 * 1000 // refetch after a while. just in case the user stays within the app for a long time without defocusing their browser window.
            }
        },
        queryCache: new QueryCache({
            onError: error => {
                if (isInvalidOrExpiredToken(error)) {
                    // escalate session errors to the global error display
                    // (normal query errors are handle by error boundaries)
                    // (background fetch errors that aren't session related currently aren't displayed)
                    showError(error);
                }
            }
        }),
        mutationCache: new MutationCache({
            onError: error => {
                if (isInvalidOrExpiredToken(error)) {
                    // escalate session errors to the global error display
                    // (other mutation errors should already be being handled by their calling code)
                    showError(error);
                }
            }
        })
    })
);

const Root = () => {
    return (
        <StrictMode>
            <QueryClientProvider client={queryClient}>
                <PropagateProvider>
                    <ThemeProvider>
                        <ScrollRestorationProvider>
                            <CustomHashRouter>
                                <AuthProvider
                                    userManager={userManager}
                                    onSigninCallback={onSigninCallback}
                                >
                                    <ErrorBoundary>
                                        {/* top level app loading boundary - spinner in the middle of the screen */}
                                        <LoadingBoundary
                                            absolute={true}
                                            delayType="long"
                                        >
                                            <LoggedInUserProvider>
                                                <FirmProvider>
                                                    <PreloadProvider>
                                                        <SuspenseImageProvider>
                                                            <Router />
                                                            <Toaster />
                                                        </SuspenseImageProvider>
                                                    </PreloadProvider>
                                                </FirmProvider>
                                            </LoggedInUserProvider>
                                        </LoadingBoundary>
                                    </ErrorBoundary>
                                    <GlobalErrorDisplay />
                                </AuthProvider>
                            </CustomHashRouter>
                        </ScrollRestorationProvider>
                    </ThemeProvider>
                </PropagateProvider>
            </QueryClientProvider>
        </StrictMode>
    );
};

const index = document.getElementById("index");
if (index) {
    const root = createRoot(index);
    root.render(<Root />);
}
